import React, {useEffect, useMemo, useRef} from 'react'
import moment from 'moment'
import {
  buildJobTimeline,
  segmentStatus,
  TimelineStop,
  TimelineVehicleInput,
} from './jobTimeline'

type Props = {
  open: boolean
  vehicle: TimelineVehicleInput | null
  onClose: () => void
  onRefresh: () => void
  isRefreshing?: boolean
}

function formatTime(value: string | Date | null): string {
  if (!value) return '—'
  const m = moment(value)
  return m.isValid() ? m.format('HH:mm') : '—'
}

function nodeClass(status: TimelineStop['status']): string {
  return `vm-timeline-node vm-timeline-node--${status}`
}

function segmentClass(status: 'completed' | 'in_transit' | 'upcoming'): string {
  return `vm-timeline-segment vm-timeline-segment--${status}`
}

export const JobTimelineStrip: React.FC<Props> = ({
  open,
  vehicle,
  onClose,
  onRefresh,
  isRefreshing = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLDivElement>(null)

  const model = useMemo(() => buildJobTimeline(vehicle), [vehicle])

  useEffect(() => {
    if (!open || !activeRef.current) return
    activeRef.current.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'})
  }, [open, model?.activeStopIndex, model?.vehicleNumber])

  if (!open || !model || model.stops.length === 0) return null

  const {stops, activeStopIndex, vehicleProgress, vehicleNumber} = model
  const stopCount = stops.length
  const NODE_WIDTH = 148
  const trackWidth = Math.max(stopCount * NODE_WIDTH, 280)
  // Vehicle left position: each stop centered at (i + 0.5) / n of track content
  // For progress 1 at stop i → center of node i
  // For progress 0.5 at stop i → midpoint between i-1 and i
  const vehicleLeftPercent = (() => {
    if (stopCount <= 1) return 50
    const step = 100 / (stopCount - 1)
    if (vehicleProgress >= 1) return activeStopIndex * step
    if (vehicleProgress <= 0) return 0
    const from = Math.max(0, activeStopIndex - 1)
    return from * step + (activeStopIndex - from) * step * vehicleProgress
  })()

  return (
    <div className='vm-timeline-strip'>
      <div className='vm-timeline-strip__toolbar'>
        <div className='vm-timeline-strip__title'>
          <i className='bi bi-signpost-2 me-2'></i>
          Job timeline
          {vehicleNumber ? <span className='ms-2 text-gray-600'>{vehicleNumber}</span> : null}
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light ms-2 vm-timeline-refresh'
            aria-label='Refresh timeline'
            title='Refresh timeline'
            disabled={isRefreshing}
            onClick={onRefresh}
          >
            <i className={`bi bi-arrow-clockwise ${isRefreshing ? 'vm-timeline-refresh--spin' : ''}`}></i>
          </button>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-icon btn-light'
          aria-label='Close timeline'
          onClick={onClose}
        >
          <i className='bi bi-x-lg'></i>
        </button>
      </div>

      <div className='vm-timeline-strip__scroll' ref={scrollRef}>
        <div className='vm-timeline-strip__track' style={{width: trackWidth}}>
          <div className='vm-timeline-strip__line-row'>
            {stops.slice(0, -1).map((stop, i) => {
              const next = stops[i + 1]
              return (
                <div
                  key={`seg-${stop.id}`}
                  className={segmentClass(segmentStatus(stop, next))}
                  style={{flex: 1}}
                />
              )
            })}
            <div
              className='vm-timeline-vehicle'
              style={{left: `${vehicleLeftPercent}%`}}
              title={vehicleNumber || 'Vehicle'}
            >
              <i className='bi bi-truck'></i>
              <span>{vehicleNumber || 'Unit'}</span>
            </div>
          </div>

          <div className='vm-timeline-strip__nodes'>
            {stops.map((stop, index) => (
              <div
                key={stop.id}
                className={nodeClass(stop.status)}
                ref={index === activeStopIndex ? activeRef : undefined}
                style={{flex: 1}}
                title={`${stop.typeLabel}: ${stop.name}\nIn: ${formatTime(
                  stop.arrival
                )}  Out: ${formatTime(stop.departure)}`}
              >
                <div className='vm-timeline-node__dot' />
                <div className='vm-timeline-node__type'>{stop.typeLabel}</div>
                <div className='vm-timeline-node__name'>{stop.name}</div>
                <div className='vm-timeline-node__times'>
                  {formatTime(stop.arrival)} / {formatTime(stop.departure)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
